import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { IconType } from '~/components/atom/Icon';
import DropdownListContainer from '~/components/molecule/Dropdown/components/DropdownListContainer';
import type { Option } from '~/components/molecule/Dropdown';
import Button from '~/components/atom/Button';
import type { InteractiveAppearance } from '~/styles/constants';

export type Props = {
  options?: Array<Option>;
  icon: IconType;
  selectedOption?: string | number;
  appearance?: InteractiveAppearance;
  label?: string;
};

const OptionsButton: React.FCC<Props> = ({
  dataTestId,
  icon,
  label,
  options = [],
  selectedOption,
  appearance = 'tertiary',
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Container>
      <Button
        icon={icon}
        label={label}
        active={showOptions}
        size="small"
        dataTestId={dataTestId}
        appearance={appearance}
        ghost
        ref={buttonRef}
        onClick={() => {
          setShowOptions(prev => !prev);
        }}
      />

      <DropdownListContainer
        options={options}
        dropdownListOpen={showOptions}
        selectedOptionIdx={
          options?.findIndex(option => option.payload === selectedOption) || 0
        }
        onChange={({ option }) => {
          if (option.onClickAction) {
            option.onClickAction(option.payload);
          }

          setShowOptions(false);
        }}
        onClickOutside={() => setShowOptions(false)}
        onClose={() => setShowOptions(false)}
        loading={false}
        openerRef={buttonRef}
      />
    </Container>
  );
};

const Container = styled.div<{}>`
  position: relative;
`;

export const OptionLabel = styled.div<{}>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;

    /* Pass margin to icons on the left in the options button */
    & > :first-child {
      margin-right: ${theme.space('xxs')};
    }
  `,
);

export default OptionsButton;
